import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import PersonalProfileDashboard from "./formPages/personalProfileDashboard";
import PrimaryContactProfileDashboard from "./formPages/primaryContactProfileDashboard";
import BusinessProfileDashboard from "./formPages/businessProfileDashboard";
import DocumentsProfileDashboard from "./formPages/documentsProfileDashboard";
import BrandIdentityProfileDashboard from "./formPages/brandIdentityProfileDashboard";
import PrivateLinkDashboard from "./formPages/privateLinkDashboard";
import { privatePortalConfigured } from "./formPages/privateLinkDashboard/portalUrl";
import { Account, Vendor } from "pages/userAuthentication/signUp";
import Tabs, { TabsProps } from "components/basic/tabs";
import { useMeQuery, VendorVerification } from "generated/graphql";
import { emptyDashboardProfileData, loadProfileData } from "./profileData";
import { useNavigate, useLocation } from "react-router-dom";
import logger from "utils/logger";
import ApplicationNotice from "./components/ApplicationNotice";

// Tabs hidden from an application fall back to the first one.
const applicationTab = (tab: string) =>
  ["business", "documents"].includes(tab) ? tab : "primary_contact";

export interface DashboardProfileData {
  account: Account;
  vendor: Vendor | null;
}

interface ProfileProps {
  /** The vendor application of a rejected applicant, edited before resubmitting. */
  application?: boolean;
}

const Profile: React.FC<ProfileProps> = ({ application = false }) => {
  const navigate = useNavigate();
  const { t } = useTranslation("profile");
  const location = useLocation();

  // /dashboard/profile/business → "business"; unknown tabs show the first one.
  const selectedProfileTab =
    location.pathname.split("/")[3] || "primary_contact";
  const basePath = application ? "application" : "profile";
  const setSelectedProfileTab = (tab: string) =>
    navigate(`/dashboard/${basePath}/${tab}`);
  const [{ data }] = useMeQuery();
  const meData = data?.me;
  const approved =
    meData?.vendor?.verificationStatus === VendorVerification.Approved;
  const showPrivateLink = privatePortalConfigured && approved && !application;
  // A bookmarked /private_link must not render a blank page once the tab is
  // hidden, so it falls back to the default tab.
  const activeProfileTab =
    selectedProfileTab === "private_link" && !showPrivateLink
      ? "primary_contact"
      : selectedProfileTab;

  const [dashboardProfileData, setDashboardProfileData] =
    useState<DashboardProfileData>(emptyDashboardProfileData);

  // Sync server data into local state once the query resolves (or on refetch).
  // useState initializer only runs once on mount — at that point meData is
  // undefined because the query is still in-flight, so we need this effect.
  useEffect(() => {
    if (!meData) return;
    logger.log("Profile data:", meData);
    setDashboardProfileData(loadProfileData(meData));
  }, [meData]);

  const onAccountChange = useCallback(
    (name: string, value: string | boolean) => {
      setDashboardProfileData((prev) => ({
        ...prev,
        account: { ...prev.account, [name]: value },
      }));
    },
    [],
  );

  const onVendorChange = useCallback(
    (name: string, value: string | boolean) => {
      setDashboardProfileData((prev) => ({
        ...prev,
        vendor: prev.vendor ? { ...prev.vendor, [name]: value } : prev.vendor,
      }));
    },
    [],
  );

  const tabData: TabsProps = {
    selectedTab: selectedProfileTab,
    setSelectedTab: setSelectedProfileTab,
    tabs: [
      { label: t("tabs.primary_contact"), path: "primary_contact" },
      { label: t("tabs.business"), path: "business" },
      { label: t("tabs.documents"), path: "documents" },
      ...(application
        ? []
        : [{ label: t("tabs.brand_identity"), path: "brand_identity" }]),
      // The portal only goes live once the vendor is approved.
      ...(showPrivateLink
        ? [{ label: t("tabs.private_link"), path: "private_link" }]
        : []),
    ],
  };

  if (!dashboardProfileData.vendor) {
    return (
      <div style={{ marginTop: 20 }}>
        <PersonalProfileDashboard
          dashboardProfileData={dashboardProfileData}
          onChange={onAccountChange}
        />
      </div>
    );
  }

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
      }}
    >
      {application && (
        <ApplicationNotice
          rejectionReason={meData?.vendor?.rejectionReason ?? null}
        />
      )}
      <Tabs {...tabData} />

      <div>
        {(() => {
          switch (
            application ? applicationTab(activeProfileTab) : activeProfileTab
          ) {
            case "private_link":
              return <PrivateLinkDashboard />;
            case "brand_identity":
              return (
                <BrandIdentityProfileDashboard
                  dashboardProfileData={dashboardProfileData}
                  onChange={onVendorChange}
                />
              );
            case "documents":
              return (
                <DocumentsProfileDashboard
                  basePath={basePath}
                  dashboardProfileData={dashboardProfileData}
                  onVendorChange={onVendorChange}
                />
              );
            case "business":
              return (
                <BusinessProfileDashboard
                  basePath={basePath}
                  dashboardProfileData={dashboardProfileData}
                  onAccountChange={onAccountChange}
                  onVendorChange={onVendorChange}
                />
              );
            default:
              return (
                <PrimaryContactProfileDashboard
                  basePath={basePath}
                  dashboardProfileData={dashboardProfileData}
                  onAccountChange={onAccountChange}
                  onVendorChange={onVendorChange}
                />
              );
          }
        })()}
      </div>
    </div>
  );
};

export default Profile;
